// @ts-nocheck

import React, { type ElementRef, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ResizeDetector from 'react-resize-detector';
import { useSpring } from 'react-spring/web.cjs';
import { animated } from 'react-spring/web.cjs';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useDrag } from 'react-use-gesture';
import styled, { css } from 'styled-components';

import { CloseLight } from '../../assets/svg';
import { useViewportContext } from '../../contexts/ViewportContext';
import { getPortalRootDOMElement } from '../../DOMElements';
import usePrevious from '../../hooks/usePrevious';
import theme from '../../styled-components/dark';
import { ZIndices } from '../../styled-components/dark/common';
import { Overlay } from '../Modal/Modal.style';
import ScrollView from '../ScrollView';

const ANIMATION_DURATION = 250; // ms, open animation duration
const SCROLLABLE_HEIGHT_THRESHOLD = 40;

const Wrapper = styled(animated.div)`
  position: absolute;
  overflow: hidden;
  ${({ $spaceBetweenModalAndTabBar }) => css`
    top: ${$spaceBetweenModalAndTabBar}px;
    top: calc(constant(safe-area-inset-top) ${$spaceBetweenModalAndTabBar ? `+ ${$spaceBetweenModalAndTabBar}px` : ''});
    top: calc(env(safe-area-inset-top) ${$spaceBetweenModalAndTabBar ? `+ ${$spaceBetweenModalAndTabBar}px` : ''});
  `}
  bottom: 0;
  right: 0;
  left: 0;
  background: ${theme.BasketMobileModal.modalBackgroundColor};
  z-index: ${(p) => p.zIndex || ZIndices.modal};
`;

const Header = styled.div`
  color: #fff;
  min-height: 45px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Title = styled.div`
  font-size: 1.3em;
  text-align: center;
`;

const SlideMark = styled.div`
  position: absolute;
  top: 10px;
  margin: 0 auto;
  width: 30px;
  height: 4px;
  border-radius: 2px;
  background-color: ${theme.BasketMobileModal.slideMarkBackgroundColor};
  left: calc(50% - 15px);
  z-index: 2;

  ${({ $isResume }) => {
    if ($isResume) {
      return css`
        opacity: 0.6;
      `;
    }
  }}
`;

const StyledCloseIcon = styled.div`
  position: absolute;
  right: 1em;
  top: 1em;
  transition: transform 0.2s ease-in-out;

  &:active {
    transform: scale(0.9);
  }
`;

const InnerWrap = styled.div`
  position: absolute;
  top: ${({ $marginTop }) => $marginTop || 0}px;
  left: 0;
  right: 0;
  height: calc(100% - ${({ $fixedHeight }) => $fixedHeight || 0}px);
  overflow: hidden;
`;

const StyledScrollView = styled(ScrollView)`
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  height: 100%;
`;

const Content = styled.div`
  height: 100%;
`;

// hook to handle header/content/footer heights
const useCalculateHeight = (isOpen: boolean) => {
  const ref = useRef<ElementRef<'div'> | null>(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    if (isOpen && ref.current) {
      const { height: domItemHeight } = ref.current.getBoundingClientRect();
      setHeight(domItemHeight);
    } else {
      setHeight(0);
    }
  }, [isOpen]);

  return { ref, height, setHeight };
};

// @see possible options to play with for gesture: https://use-gesture.netlify.app/docs/options/#gesture-options-1
const dragConfig = {
  useTouch: true,
  swipeDistance: [10, 10], // pixels
};

// hook to handle swipe behavior
const useSwipe = (isOpen, viewportHeight, totalModalHeight, disableSwipe, onClose) => {
  const shouldCloseSwipe = useRef<boolean>(false);
  const [{ y }, set] = useSpring(() => ({ y: isOpen ? 0 : viewportHeight }));

  useEffect(() => {
    set({ y: isOpen ? 0 : viewportHeight });
  }, [viewportHeight, isOpen, set]);

  const bind = useDrag(({ down, movement: [, my], distance, last, swipe: [, swipeY] }) => {
    if (disableSwipe) {
      return;
    }
    if (!shouldCloseSwipe.current) {
      const threshold = Math.min(viewportHeight, totalModalHeight) * 0.5;
      const stopDragAtBottom = !down && distance > threshold;
      // close on swipe top bottom or touch up at bottom
      if (swipeY === 1 || stopDragAtBottom) {
        shouldCloseSwipe.current = true;
      }
    }
    set(() => {
      if (shouldCloseSwipe.current) {
        if (last) {
          onClose();
          shouldCloseSwipe.current = false;
        }
        return { y: totalModalHeight };
      }
      return { y: down ? Math.max(my, 0) : 0 };
    });
  }, dragConfig);
  return { bind, y };
};

const useDetectAutoScroll = (shouldScrollTop, hasEnoughScrollableSpace, isKeyboardVisible) => {
  const scrollToBottomRef = useRef<ElementRef<'div'> | null>(null);
  const scrollToTopRef = useRef<ElementRef<'div'> | null>(null);

  const previousHasEnough = usePrevious(hasEnoughScrollableSpace);
  const previousShouldScrollTop = usePrevious(shouldScrollTop);

  const shouldScrollToBottom =
    scrollToBottomRef.current && isKeyboardVisible && previousHasEnough && !hasEnoughScrollableSpace;
  const shouldScrollToTop = scrollToTopRef.current && !previousShouldScrollTop && shouldScrollTop;
  const scrollRef = useMemo(() => {
    if (shouldScrollToBottom) {
      return scrollToBottomRef;
    }
    if (shouldScrollToTop) {
      return scrollToTopRef;
    }
    return null;
  }, [shouldScrollToBottom, shouldScrollToTop]);
  return { scrollRef, scrollToBottomRef, scrollToTopRef };
};

/*
On ios embed, when dragging modal, a click on below overlay can be triggred, so we want to avoid closing modal on that specific case
*/
const useDisableOverlayClick = (isOpen, headerRef, onClose) => {
  const isStartDraggingModale = useRef(false);

  const onTouchEnd = useCallback(() => {
    // we delay touch end to allow possible overlay click first
    setTimeout(() => {
      isStartDraggingModale.current = false;
    }, 100);
  }, []);

  const onTouchStart = useCallback(() => {
    isStartDraggingModale.current = true;
  }, []);

  const handleOverlayClick = () => {
    // we close modal only if we haven't start dragging modal
    if (!isStartDraggingModale.current) {
      onClose();
    }
  };

  useEffect(() => {
    const header = headerRef.current;
    if (header && isOpen) {
      header.addEventListener('touchstart', onTouchStart);
      window.addEventListener('touchend', onTouchEnd);
    }

    return () => {
      if (header && isOpen) {
        header.removeEventListener('touchstart', onTouchStart);
        window.removeEventListener('touchend', onTouchEnd);
      }
    };
  }, [onTouchEnd, onTouchStart, headerRef, isOpen]);
  return handleOverlayClick;
};

type MobileModalProps = {
  children: React.ReactNode;
  onClose: () => void;
  disableSwipe: boolean;
  showSwipeIndicator: boolean;
  isOpen: boolean;
  shouldScrollTop: boolean;
  fullHeight: boolean;
  title?: string;
};

const MobileModal = ({
  children,
  onClose,
  isOpen = false,
  disableSwipe = false,
  showSwipeIndicator = false,
  shouldScrollTop,
  fullHeight = false,
  title,
}: MobileModalProps) => {
  const { height: viewportHeight, navbarHeight } = useViewportContext();

  const { ref: headerRef, height: headerHeight, setHeight: setHeaderHeight } = useCalculateHeight(isOpen);
  const { ref: footerRef, height: footerHeight, setHeight: setFooterHeight } = useCalculateHeight(isOpen);
  const { ref: contentRef, height: contentHeight, setHeight: setContentHeight } = useCalculateHeight(isOpen);

  // the safe area values are not included here because it returns sometimes 0 at the load.
  // @see Basket.jsx to see the add of those safe area values into the padding-bottom property

  const hasEnoughScrollableSpace = useMemo(() => {
    const scrollableHeight = viewportHeight - (footerHeight + headerHeight + navbarHeight);
    return scrollableHeight > SCROLLABLE_HEIGHT_THRESHOLD;
  }, [viewportHeight, footerHeight, headerHeight, navbarHeight]);

  const totalModalHeight = footerHeight + headerHeight + contentHeight;

  const { bind, y } = useSwipe(isOpen, viewportHeight, totalModalHeight, disableSwipe, onClose);

  const { scrollRef, scrollToBottomRef, scrollToTopRef } = useDetectAutoScroll(
    shouldScrollTop,
    hasEnoughScrollableSpace,
  );

  // for devices with notches, when the basket modal has a lot of items, it hides the navigation bar.
  // To avoid that, we add a limit
  const LIMIT_TO_SUBSTRACT_SAFE_AREA_INSET = 30;

  const getSpaceBetweenModalAndTabBar = () => {
    const spaceBetweenModalAndTabBar = viewportHeight - totalModalHeight - navbarHeight;
    if (
      spaceBetweenModalAndTabBar > LIMIT_TO_SUBSTRACT_SAFE_AREA_INSET &&
      hasEnoughScrollableSpace &&
      totalModalHeight < viewportHeight &&
      !fullHeight
    ) {
      // to avoid returning negatives values, we use Math.max() with 0 as first parameter
      return Math.max(0, spaceBetweenModalAndTabBar);
    }

    return 0;
  };

  const spaceBetweenModalAndTabBar = getSpaceBetweenModalAndTabBar();

  const onResize = useCallback(() => {
    if (isOpen) {
      if (headerRef.current) {
        const { height: headerHeight } = headerRef.current.getBoundingClientRect();
        setHeaderHeight(headerHeight);
      }
      if (footerRef.current) {
        const { height: footerHeight } = footerRef.current.getBoundingClientRect();
        setFooterHeight(footerHeight);
      }
      if (contentRef.current) {
        const { height: contentHeight } = contentRef.current.getBoundingClientRect();
        setContentHeight(contentHeight);
      }
    }
  }, [isOpen, headerRef, footerRef, contentRef, setHeaderHeight, setFooterHeight, setContentHeight]);

  const handleOverlayClick = useDisableOverlayClick(isOpen, headerRef, onClose);

  return createPortal(
    <>
      {isOpen ? <Overlay onClick={handleOverlayClick} /> : null}
      <TransitionGroup>
        {isOpen ? (
          <CSSTransition timeout={ANIMATION_DURATION}>
            <Wrapper style={{ y }} $spaceBetweenModalAndTabBar={spaceBetweenModalAndTabBar}>
              <div ref={headerRef} {...bind()}>
                <Header>
                  {title ? <Title>{title}</Title> : null}
                  {showSwipeIndicator ? <SlideMark /> : null}
                  <StyledCloseIcon onClick={onClose}>
                    <CloseLight />
                  </StyledCloseIcon>
                </Header>
                <ResizeDetector handleHeight onResize={onResize} />
              </div>
              <InnerWrap
                $marginTop={headerHeight}
                $fixedHeight={(hasEnoughScrollableSpace ? footerHeight : 0) + headerHeight}
              >
                <StyledScrollView
                  elementToScrollTo={scrollRef}
                  shouldScrollToElement={!!scrollRef}
                  isOverflowing={!!scrollRef}
                >
                  <div ref={scrollToTopRef} />
                  <Content ref={contentRef}>
                    {children}
                    <ResizeDetector handleHeight onResize={onResize} />
                  </Content>
                  <div ref={scrollToBottomRef} />
                </StyledScrollView>
              </InnerWrap>
            </Wrapper>
          </CSSTransition>
        ) : null}
      </TransitionGroup>
    </>,
    getPortalRootDOMElement(),
  );
};

export default MobileModal;
